/**
 * The Liquid Glass stylesheet.
 *
 * A constant, and that is the point. Every value an operator can change is a
 * custom property set on the host by `theme.ts` through `setProperty`, so
 * nothing from outside this file is ever concatenated into it — there is no
 * interpolation here for a brand value to arrive through.
 *
 * The material, in the order the layers stack:
 *
 *   1. a translucent tint of `--geni-surface`, so the panel has a colour of
 *      its own before anything behind it is considered;
 *   2. `backdrop-filter: blur(20px) saturate(180%) contrast(105%)` — the blur
 *      is the glass, the saturation is what stops a blurred backdrop turning
 *      grey, and the contrast keeps text legible over a busy photograph;
 *   3. a specular edge: a 1px gradient border, bright where a bevel would catch
 *      the light and gone where it would not, plus an inset top highlight;
 *   4. grain, at very low opacity, because a perfectly smooth gradient reads as
 *      flat plastic and real glass has a surface;
 *   5. an accent aura behind the whole thing, which is what makes the panel look
 *      lit rather than pasted on.
 *
 * Light and dark are the same construction with the roles swapped, not two
 * stylesheets. Dark glass tints toward black and raises the highlight; light
 * glass tints toward white and lowers it.
 */

export const WIDGET_STYLES = String.raw`
/* ── Tokens ────────────────────────────────────────────────────────────────
   Only the six in theme.ts come from the operator. Everything below is derived,
   so a rebrand is one attribute rather than a fork of this file. */
:host {
  --geni-accent: #5223d3;
  --geni-accent-deep: #471faa;
  --geni-surface: #ffffff;
  --geni-text: #16141f;
  --geni-radius: 26px;
  --geni-font: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Inter, Roboto, "Helvetica Neue", Arial, sans-serif;

  /* Light glass: a white tint, a bright bevel, a soft shadow. */
  --geni-glass: color-mix(in srgb, var(--geni-surface) 96%, transparent);
  --geni-glass-raised: color-mix(in srgb, var(--geni-surface) 99%, transparent);
  --geni-glass-sunken: color-mix(in srgb, var(--geni-surface) 70%, transparent);
  --geni-bevel-bright: rgba(255, 255, 255, 0.85);
  --geni-bevel-dim: rgba(255, 255, 255, 0.18);
  --geni-hairline: color-mix(in srgb, var(--geni-text) 10%, transparent);
  --geni-text-muted: color-mix(in srgb, var(--geni-text) 68%, transparent);
  --geni-text-faint: color-mix(in srgb, var(--geni-text) 52%, transparent);
  --geni-on-accent: #ffffff;
  --geni-grain: 0.035;
  --geni-aura: 0.5;
  --geni-shadow:
    0 2px 6px rgba(16, 12, 32, 0.05),
    0 12px 28px rgba(16, 12, 32, 0.1),
    0 32px 68px rgba(16, 12, 32, 0.14);

  /* The one easing everything uses. Overshoots slightly, which is what makes a
     movement read as physical rather than as a transition. */
  --geni-spring: cubic-bezier(0.22, 1.16, 0.36, 1);

  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2147483000;
  font-family: var(--geni-font);
  color: var(--geni-text);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-variant-numeric: tabular-nums;
}

/* Dark glass. Same layers, swapped roles: the tint goes to black, the bevel
   drops to something a dark backdrop can still show, and the aura comes up
   because a glow reads as light only against something darker than it. */
@media (prefers-color-scheme: dark) {
  :host(:not([theme="light"])) {
    --geni-surface: #0e0b18;
    --geni-text: #f4f2fb;
    --geni-glass: color-mix(in srgb, var(--geni-surface) 95%, transparent);
    --geni-glass-raised: color-mix(in srgb, var(--geni-surface) 98%, transparent);
    --geni-glass-sunken: color-mix(in srgb, var(--geni-surface) 66%, transparent);
    --geni-bevel-bright: rgba(255, 255, 255, 0.34);
    --geni-bevel-dim: rgba(255, 255, 255, 0.06);
    --geni-hairline: color-mix(in srgb, var(--geni-text) 14%, transparent);
    --geni-grain: 0.055;
    --geni-aura: 0.75;
    --geni-shadow:
      0 2px 8px rgba(0, 0, 0, 0.36),
      0 16px 40px rgba(0, 0, 0, 0.46),
      0 40px 90px rgba(0, 0, 0, 0.5);
  }
}

/* An operator forcing dark on a light page, and the exact same block. Written
   out rather than shared with the media query because the two conditions are
   genuinely different questions — what the page prefers, and what the operator
   asked for — and one must be able to win. */
:host([theme="dark"]) {
  --geni-surface: #0e0b18;
  --geni-text: #f4f2fb;
  --geni-glass: color-mix(in srgb, var(--geni-surface) 95%, transparent);
  --geni-glass-raised: color-mix(in srgb, var(--geni-surface) 98%, transparent);
  --geni-glass-sunken: color-mix(in srgb, var(--geni-surface) 66%, transparent);
  --geni-bevel-bright: rgba(255, 255, 255, 0.34);
  --geni-bevel-dim: rgba(255, 255, 255, 0.06);
  --geni-hairline: color-mix(in srgb, var(--geni-text) 14%, transparent);
  --geni-grain: 0.055;
  --geni-aura: 0.75;
  --geni-shadow:
    0 2px 8px rgba(0, 0, 0, 0.36),
    0 16px 40px rgba(0, 0, 0, 0.46),
    0 40px 90px rgba(0, 0, 0, 0.5);
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

/* ── The material ──────────────────────────────────────────────────────────
   The tint and the blur live on a layer of their own, inside the rounded
   element rather than on it. That is not tidiness — it is the fix for a real
   artifact.

   An element carrying BOTH border-radius and backdrop-filter has its filtered
   backdrop clipped to the border *box* while its background and border follow
   the radius. The corner regions outside the curve are therefore painted with
   a blurred, saturated copy of the page and no tint over it, so a bright
   hard-edged corner sits just outside each rounded one — subtle against a busy
   backdrop, obvious against a plain one, and much worse mid-animation.

   An overflow:hidden on that same element cannot help, which is the trap:
   overflow clips an element's descendants, never its own backdrop. Clipping a
   *descendant's* backdrop is exactly what it does do — so the filter goes on a
   child and the rounded, overflow-hidden parent clips it to the right shape.

   The layer is behind everything: negative z-index puts it under the panel's
   in-flow content while keeping it inside the panel's stacking context. */
.geni-glass-layer {
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background: var(--geni-glass);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
}

/* The specular edge: a 1px gradient ring, masked so only the border shows.
   Bright at the top-left where a bevel would catch the light, absent through
   the middle, returning faintly at the bottom-right as bounced light. A flat
   1px border is the tell that a glass panel is a rectangle with a blur on it. */
.geni-panel::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(
    145deg,
    var(--geni-bevel-bright) 0%,
    var(--geni-bevel-dim) 26%,
    transparent 52%,
    var(--geni-bevel-dim) 78%,
    color-mix(in srgb, var(--geni-accent) 26%, transparent) 100%
  );
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}

/* Grain. Real glass has a surface; a pure gradient reads as plastic. Kept below
   6% because the moment it is visible as noise it looks like a dirty screen. */
.geni-grain {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  opacity: var(--geni-grain);
  pointer-events: none;
  z-index: 1;
  mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}

/* The aura sits *behind* the panel and is the reason it looks lit. Two accent
   blobs, offset, drifting slowly against each other. */
.geni-aura {
  position: absolute;
  inset: -18% -12% -12%;
  border-radius: 50%;
  opacity: var(--geni-aura);
  pointer-events: none;
  /* Below the glass layer, so it is a glow seen *through* the material rather
     than a coloured wash sitting on top of it. Stated rather than left to DOM
     order, because the order of two decorative divs is exactly the kind of
     thing a later edit reshuffles without noticing. */
  z-index: -2;
  filter: blur(46px);
  background:
    radial-gradient(46% 40% at 22% 8%, color-mix(in srgb, var(--geni-accent) 52%, transparent), transparent 70%),
    radial-gradient(42% 38% at 82% 92%, color-mix(in srgb, var(--geni-accent-deep) 44%, transparent), transparent 72%);
  animation: geni-aura-drift 14s ease-in-out infinite alternate;
}

@keyframes geni-aura-drift {
  from { transform: translate3d(-2%, -1%, 0) scale(1); }
  to   { transform: translate3d(3%, 2%, 0) scale(1.08); }
}

/* ── Launcher ──────────────────────────────────────────────────────────────
   The collapsed state from the mockup: a round accent button in the corner. */
.geni-launcher {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 58px;
  padding: 0 8px;
  border: 0;
  border-radius: 999px;
  font: inherit;
  font-size: 15px;
  font-weight: 560;
  letter-spacing: -0.01em;
  color: var(--geni-on-accent);
  cursor: pointer;
  background: linear-gradient(150deg, var(--geni-accent), var(--geni-accent-deep));
  box-shadow:
    var(--geni-shadow),
    inset 0 1px 0 rgba(255, 255, 255, 0.4),
    inset 0 -1px 0 rgba(0, 0, 0, 0.14);
  transition:
    transform 0.42s var(--geni-spring),
    box-shadow 0.42s var(--geni-spring),
    padding 0.42s var(--geni-spring);
  animation: geni-launcher-in 0.72s var(--geni-spring) both;
}

/* The label sits beside the stage rather than being revealed on hover. A
   teaser nobody can read until they point at it is a teaser that does no
   teasing — and on a phone there is no hover to point with. */
.geni-launcher-label {
  white-space: nowrap;
  padding-right: 4px;
  /* The angle is animated on the host; see geni-label-turn. The transform is
     here rather than there because only the name should turn — the pill
     carries a backdrop-filter, and rotating that drags the whole frosted
     backdrop round with it. The fallback matters: where the angle is not a
     registered property this reads 0deg and the label simply stands still. */
  transform: perspective(420px) rotateY(var(--geni-turn, 0deg));
}

/* A turn of the name, once every ten seconds.
 *
 * The launcher sits in the corner of somebody else's page and is easy not to
 * notice; this is the one thing on it that moves while nothing is happening.
 *
 * **The animation is on the host, and it has to be.** Written first as a
 * keyframe on the label itself, where it never once ran: paint() rebuilds the
 * whole tree with replaceChildren on every state change, the teaser loop
 * produces one every 3.4 to 4.4 seconds, and an animation whose motion lives in
 * its last tenth never survives to reach it. The host is the one node that is
 * never replaced, so the cycle runs there and the label — however many times it
 * is rebuilt — reads the angle it is currently at.
 *
 * That also rules out a timer: a second one beside the teaser's would be a
 * second thing to cancel on close, on removal and on every language change, and
 * the two would drift apart on any frame the main thread was busy.
 *
 * Nine seconds still, then one turning, rather than a slow constant spin: it
 * has to read as the control asking to be looked at once, not as an ornament
 * that never settles.
 *
 * A full 360 rather than a flip to 180, because the text is mirrored halfway
 * through and a name left standing backwards is a broken widget.
 *
 * Removed entirely under prefers-reduced-motion, by the blanket rule at the end
 * of this file. Nothing is lost when it goes: the pill still says its name.
 */
@property --geni-turn {
  syntax: "<angle>";
  initial-value: 0deg;
  inherits: true;
}

:host {
  animation: geni-label-turn 10s linear infinite;
}

@keyframes geni-label-turn {
  0%,
  90% {
    --geni-turn: 0deg;
    animation-timing-function: cubic-bezier(0.62, 0, 0.3, 1);
  }
  100% {
    --geni-turn: 360deg;
  }
}

.geni-launcher:hover,
.geni-launcher:focus-visible {
  transform: translateY(-3px) scale(1.03);
}

.geni-launcher:active {
  transform: translateY(-1px) scale(0.985);
}

.geni-launcher-mark {
  flex: none;
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

.geni-launcher-mark svg {
  width: 24px;
  height: 24px;
  fill: currentColor;
}

/* ── The pill, as a visionOS material ────────────────────────────────────────
   Dark translucent glass over whatever page it is floating on, a paper-thin
   white stroke, and a specular highlight along the top edge. The sport is a
   soft ambient glow rather than a coloured fill: a solid sport colour makes the
   control an object *about* football, where a tint makes it a control sitting
   in a football context — which is the distinction visionOS materials draw.

   It stays dark in light mode, deliberately, and unlike everything inside the
   panel. This is the one element that floats on the OPERATOR's page rather than
   inside our own surface, and a light frosted pill on a light hero image is a
   pill nobody can find. */
.geni-launcher--staged {
  width: 180px;
  height: 60px;
  padding: 0 18px 0 10px;
  gap: 10px;
  justify-content: flex-start;
  overflow: hidden;
  isolation: isolate;

  background: rgba(18, 18, 22, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.15);
  /* The blur is NOT here. See .geni-launcher-glass. */
  box-shadow:
    0 12px 34px rgba(0, 0, 0, 0.42),
    0 2px 8px rgba(0, 0, 0, 0.28);
}

/* The frosted material, on a child the rounded button clips.

   An element carrying both a border-radius and a backdrop-filter paints its
   filtered backdrop into the square border box, so a hard corner shows just
   outside each rounded one — and this is the most rounded thing in the widget,
   so it would have shown it worst. The panel learned this in chunk 13; a rule
   in the test suite forbids the pair, and this is why. */
.geni-launcher-glass {
  position: absolute;
  inset: 0;
  z-index: -3;
  pointer-events: none;
  background: rgba(16, 16, 20, 0.52);
  backdrop-filter: blur(20px) saturate(170%);
  -webkit-backdrop-filter: blur(20px) saturate(170%);
}

/* The sport, as light rather than as paint. Strongest behind the glyph and
   gone by the middle of the capsule, so the label keeps a neutral ground to be
   read against — a tint running under the text is what makes a control like
   this read as a toy. */
.geni-launcher-glow {
  position: absolute;
  inset: 0;
  z-index: -2;
  pointer-events: none;
  background:
    radial-gradient(58% 130% at 16% 50%,
      color-mix(in srgb, var(--geni-sport-glow, #7c6cff) 42%, transparent),
      transparent 72%);
}

/* The specular highlight: light gathering along the top, the way a curved glass
   surface catches a source above it.

   A gradient and nothing else. There used to be a one-pixel inset hairline at
   0.4 white over it, which is how the same effect is drawn on a light surface —
   but this pill is dark and translucent, and on it that line stopped reading as
   a lit edge and started reading as a white bar laid across the top. The
   staged pill already carries a 1px border at 0.15, so the top edge is defined
   without it. */
.geni-launcher-sheen {
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.13), transparent 46%);
}

/* Pure white, and a shade tighter than the panel's type. This is the one place
   in the widget where the operator's palette deliberately does not apply: the
   label has to stay legible over a page we have never seen, and a brand colour
   on translucent glass is the first thing to disappear. */
.geni-launcher--staged .geni-launcher-label {
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.012em;
  -webkit-font-smoothing: antialiased;
}

/* The stage: no container, no fill, no border. The glyph sits directly on the
   capsule's own material, which is what stops the left half reading as a second
   panel inset into the first. */
.geni-stage {
  /* The default arc, and it sits HERE rather than on the ball for a reason that
     is load-bearing rather than tidy. Custom properties inherit, but a
     declaration on an element beats one inherited from its ancestor — so
     defaults on .geni-stage-ball would win over every variant class, which
     sits on this container, and all fifteen actions would play the identical
     shot. That is what happened first time round, and it reads as a design
     choice rather than a bug, so a test pins where these live. */
  --t-x1: 14px; --t-y1: 18px;
  --t-x2: 40px; --t-y2: 17px;
  --t-arc: 9px;

  flex: none;
  display: block;
  width: 64px;
  height: 42px;
}

.geni-stage svg { width: 100%; height: 100%; display: block; overflow: visible; }

/* The glyph never leaves. For most of the cycle this is a resting icon, which
   is what a control should look like when nobody is watching it move. */
.geni-stage-glyph {
  fill: none;
  stroke: color-mix(in srgb, var(--geni-sport-glow, #ffffff) 55%, #ffffff);
  stroke-width: 1.5;
}

.geni-stage-glyph-detail {
  fill: none;
  stroke: color-mix(in srgb, var(--geni-sport-glow, #ffffff) 55%, #ffffff);
  stroke-width: 1.1;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.72;
}

/* Where the action lands. Barely there at rest, so the capsule is a glyph and a
   whisper rather than a diagram. */
.geni-stage-target {
  fill: none;
  stroke: #ffffff;
  stroke-width: 1.2;
  opacity: 0.22;
  transform-box: fill-box;
  transform-origin: center;
}

.geni-stage-ball { fill: #ffffff; stroke: none; }

/* Each sport's light. One colour per board, and the same one lights the glow
   and the glyph — which is what makes a glance tell tennis from golf before any
   of the shapes resolve. */
.geni-sport--SOCCER { --geni-sport-glow: #30d17a; }
/* Red, and chosen against basketball rather than against the ball. Leather
   brown is the obvious colour and it was the first one here; it is also
   basketball's orange at a lower brightness — the same hue to within three
   degrees — so the two boards lit the launcher identically and the glow
   stopped telling them apart, which is the one job it has. Red is the
   nearest free hue and the sport's own besides. */
.geni-sport--AMERICAN_FOOTBALL { --geni-sport-glow: #e0364f; }
.geni-sport--BASKETBALL { --geni-sport-glow: #ff9d4d; }
.geni-sport--TENNIS { --geni-sport-glow: #4da3ff; }
.geni-sport--GOLF { --geni-sport-glow: #a8e05f; }
.geni-sport--ESPORTS { --geni-sport-glow: #6ee7ff; }

/* ── One action, fifteen ways ────────────────────────────────────────────────
   Every action is the same two animations — a travelling dot and a target that
   answers — differing only in four numbers: where the dot starts, where it
   lands, and how high it arcs. Custom properties resolve at computed-value time
   and do not change while the animation runs, so one keyframe block serves all
   of them. Fifteen blocks would have been fifteen times the stylesheet for the
   same motion. */
.geni-stage-ball {
  animation: geni-stage-travel var(--geni-stage-ms, 1400ms) cubic-bezier(0.3, 0, 0.5, 1) both;
}

.geni-stage-target {
  animation: geni-stage-strike var(--geni-stage-ms, 1400ms) ease-out both;
}

@keyframes geni-stage-travel {
  0%, 14%   { transform: translate(var(--t-x1), var(--t-y1)); opacity: 0; }
  18%       { opacity: 1; }
  /* The apex, halfway across and --t-arc above the straight line between the
     two ends. Two steps rather than a path, because a translate can be
     interpolated and a motion path cannot be varied by a custom property. */
  36%       { transform: translate(calc((var(--t-x1) + var(--t-x2)) / 2),
                                   calc((var(--t-y1) + var(--t-y2)) / 2 - var(--t-arc))); }
  56%       { transform: translate(var(--t-x2), var(--t-y2)); opacity: 1; }
  66%, 100% { transform: translate(var(--t-x2), var(--t-y2)); opacity: 0; }
}

@keyframes geni-stage-strike {
  0%, 54% { transform: scale(1); opacity: 0.22; }
  62%     { transform: scale(1.3); opacity: 0.9; }
  78%     { transform: scale(1); opacity: 0.22; }
  100%    { transform: scale(1); opacity: 0.22; }
}

/* Football: two corners from opposite flanks, and one run at the goal. */
.geni-stage--left-corner { --t-x1: 12px; --t-y1: 9px; --t-x2: 38px; --t-y2: 17px; --t-arc: 9px; }
.geni-stage--right-corner { --t-x1: 12px; --t-y1: 26px; --t-x2: 38px; --t-y2: 17px; --t-arc: 10px; }
.geni-stage--dribble { --t-x1: 16px; --t-y1: 18px; --t-x2: 38px; --t-y2: 17px; --t-arc: 3px; }

/* American football: a bomb that hangs, a slant thrown nearly flat, and a snap
   that travels a couple of yards. The arc is what separates them — a thrown
   ball is the only thing on this stage that is not struck. */
.geni-stage--deep-bomb { --t-x1: 13px; --t-y1: 20px; --t-x2: 38px; --t-y2: 15px; --t-arc: 16px; }
.geni-stage--slant { --t-x1: 15px; --t-y1: 21px; --t-x2: 38px; --t-y2: 14px; --t-arc: 4px; }
.geni-stage--snap { --t-x1: 26px; --t-y1: 19px; --t-x2: 38px; --t-y2: 17px; --t-arc: 2px; }

/* Basketball: a long three, a dunk that barely leaves the hand, one off the
   board. */
.geni-stage--three-pointer { --t-x1: 14px; --t-y1: 16px; --t-x2: 38px; --t-y2: 17px; --t-arc: 13px; }
.geni-stage--slam-dunk { --t-x1: 28px; --t-y1: 11px; --t-x2: 38px; --t-y2: 17px; --t-arc: 1px; }
.geni-stage--bank-shot { --t-x1: 14px; --t-y1: 19px; --t-x2: 38px; --t-y2: 12px; --t-arc: 7px; }

/* Tennis: flat across the court, down from above, up over the head. */
.geni-stage--cross-court { --t-x1: 15px; --t-y1: 22px; --t-x2: 38px; --t-y2: 14px; --t-arc: 5px; }
.geni-stage--smash { --t-x1: 15px; --t-y1: 10px; --t-x2: 38px; --t-y2: 25px; --t-arc: -2px; }
.geni-stage--lob { --t-x1: 15px; --t-y1: 22px; --t-x2: 38px; --t-y2: 18px; --t-arc: 15px; }

/* Golf: a drive that climbs, a chip that pops, a putt that rolls flat. */
.geni-stage--drive { --t-x1: 15px; --t-y1: 21px; --t-x2: 38px; --t-y2: 19px; --t-arc: 14px; }
.geni-stage--chip { --t-x1: 20px; --t-y1: 22px; --t-x2: 38px; --t-y2: 19px; --t-arc: 9px; }
.geni-stage--putt { --t-x1: 17px; --t-y1: 22px; --t-x2: 38px; --t-y2: 19px; --t-arc: 0px; }

/* CS:GO: the shot snaps rather than arcs, so every arc here is flat and the
   difference is where it comes from and how far it travels. */
.geni-stage--flick { --t-x1: 16px; --t-y1: 25px; --t-x2: 38px; --t-y2: 17px; --t-arc: 0px; }
.geni-stage--spray { --t-x1: 16px; --t-y1: 12px; --t-x2: 38px; --t-y2: 17px; --t-arc: -3px; }
.geni-stage--clutch { --t-x1: 16px; --t-y1: 17px; --t-x2: 38px; --t-y2: 17px; --t-arc: 0px; }

/* The online dot from the mockup, with the halo that says the assistant is
   listening rather than merely present. Sits in the capsule's top-right rather
   than the circle's bottom-right now that the launcher is a pill.

   Held further in than the corner suggests, because the pill is a capsule and
   the staged variant clips to it. At the dot's height the curve has already
   eaten 5.6px of the box, and the dot carries a 2px ring of its own — parked at
   7/9 the ring met the curve with 2.4px to spare and the dot read as shaved
   flat on its right. Measured rather than nudged: at 11/13 the same ring
   clears the edge by 7px, which is enough for the halo to look round while it
   is still opaque enough to see. */
.geni-launcher-status {
  position: absolute;
  top: 11px;
  right: 13px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #34d17d;
  box-shadow: 0 0 0 2px rgba(18, 18, 22, 0.9);
}

.geni-launcher-status::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: inherit;
  animation: geni-ping 2.6s ease-out infinite;
}

@keyframes geni-ping {
  0%   { transform: scale(1); opacity: 0.65; }
  70%  { transform: scale(2.6); opacity: 0; }
  100% { transform: scale(2.6); opacity: 0; }
}

/* ── Panel ─────────────────────────────────────────────────────────────────  */
.geni-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(392px, calc(100vw - 32px));
  max-height: min(680px, calc(100vh - 48px));
  border-radius: var(--geni-radius);
  /* Clips the glass layer above to the rounded shape. See the note there for
     why the filter is on a child and not here. */
  overflow: hidden;
  box-shadow: var(--geni-shadow), inset 0 1px 0 var(--geni-bevel-bright);
  /* The entrance: a spring up from below, landing with a shimmer pass across
     the glass. The attention-grabber.

     There is deliberately no filter in the keyframes, and it is not a taste
     call: a filter on an ancestor makes that ancestor a backdrop root, so the
     glass layer inside can only sample the panel itself — the blur has nothing
     behind it to work on and the material goes flat for the length of the
     animation, then pops. It also badly worsened the corner artifact the glass
     layer exists to fix. Transform and opacity give the same motion and neither
     is a filter.

     "backwards" and NOT "both", which is the other half of the same lesson and
     took far longer to find. Under "both" the element keeps applying the last
     keyframe for ever — and an animated "transform: none" resolves to the
     identity matrix rather than to the keyword, so the panel was left carrying
     "matrix(1, 0, 0, 1, 0, 0)" permanently. A transform makes an element a
     backdrop root exactly as a filter does, so the glass layer inside could
     only ever sample the panel itself: the blur had nothing behind it to work
     on and has been doing nothing since the entrance was written. The material
     was a flat tint pretending to be glass, which is why the page bled through
     it no matter how the alpha was tuned.

     "backwards" applies the first keyframe before the animation starts, which
     is all that was ever needed — after it finishes the element goes back to
     its own style, and its own style has no transform. */
  transform-origin: 100% 100%;
}

/* Only when the panel arrives, never on the repaints after it.

   paint() rebuilds the tree with replaceChildren, so a repaint hands the
   entrance a brand new node and it runs again from zero opacity — the panel
   blinking out and fading back in under somebody reading it. Opening the panel
   fires two paints on its own: one for the state change and one when the
   session settles about eight hundred milliseconds later, which is exactly how
   long the flash took to arrive. The class is set for the first of those and
   for nothing after. */
.geni-panel--entering {
  animation: geni-panel-in 0.86s var(--geni-spring) backwards;
}

@keyframes geni-panel-in {
  0% {
    opacity: 0;
    transform: translate3d(0, 34px, 0) scale(0.9);
  }
  52% {
    opacity: 1;
    transform: translate3d(0, -7px, 0) scale(1.015);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

/* A single band of light travelling across the glass as it lands. Runs once —
   a repeating shimmer stops reading as a material and starts reading as a
   loading skeleton. */
.geni-shimmer {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
  pointer-events: none;
  z-index: 3;
}

.geni-shimmer::after {
  content: "";
  position: absolute;
  top: -60%;
  bottom: -60%;
  width: 42%;
  left: -55%;
  background: linear-gradient(
    100deg,
    transparent,
    color-mix(in srgb, var(--geni-surface) 68%, transparent) 42%,
    color-mix(in srgb, var(--geni-surface) 82%, transparent) 50%,
    transparent
  );
  transform: skewX(-16deg);
  animation: geni-shimmer 1.5s cubic-bezier(0.32, 0.08, 0.24, 1) 0.28s both;
}

@keyframes geni-shimmer {
  from { left: -55%; opacity: 0; }
  18%  { opacity: 1; }
  to   { left: 118%; opacity: 0; }
}

/* Contents rise a beat after the panel, in sequence. The stagger is what makes
   the entrance feel authored rather than mechanical.

   Every decorative layer is excluded by name. Letting the glass layer rise with
   the content would slide the panel's own surface up from under its frame — the
   material has to be there before anything can sit on it. The nth-child delays
   count those four layers, so adding or removing one means renumbering here. */
.geni-panel > *:not(.geni-aura):not(.geni-glass-layer):not(.geni-grain):not(.geni-shimmer) {
  animation: geni-rise 0.62s var(--geni-spring) both;
}
.geni-panel > *:nth-child(5) { animation-delay: 0.16s; }
.geni-panel > *:nth-child(6) { animation-delay: 0.22s; }
.geni-panel > *:nth-child(7) { animation-delay: 0.28s; }
.geni-panel > *:nth-child(8) { animation-delay: 0.34s; }

@keyframes geni-rise {
  from { opacity: 0; transform: translate3d(0, 12px, 0); }
  to   { opacity: 1; transform: none; }
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.geni-header {
  position: relative;
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 16px 14px;
  border-bottom: 1px solid var(--geni-hairline);
  background: linear-gradient(
    to bottom,
    color-mix(in srgb, var(--geni-surface) 30%, transparent),
    transparent
  );
}

.geni-brand {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.geni-mark {
  flex: none;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  color: #fff;
  background: linear-gradient(150deg, var(--geni-accent), var(--geni-accent-deep));
  box-shadow:
    0 4px 12px color-mix(in srgb, var(--geni-accent) 38%, transparent),
    inset 0 1px 0 rgba(255, 255, 255, 0.34);
}

.geni-mark svg {
  width: 22px;
  height: 22px;
  fill: currentColor;
}

.geni-brand-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.geni-title {
  margin: 0;
  font-size: 15px;
  font-weight: 620;
  letter-spacing: -0.015em;
  line-height: 1.25;
}

.geni-subtitle {
  margin: 0;
  font-size: 11.5px;
  font-weight: 480;
  letter-spacing: 0.005em;
  color: var(--geni-text-muted);
  display: flex;
  align-items: center;
  gap: 5px;
}

.geni-subtitle::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #34d17d;
  box-shadow: 0 0 0 3px color-mix(in srgb, #34d17d 22%, transparent);
}

.geni-header-actions {
  margin-left: auto;
  display: flex;
  gap: 6px;
}

.geni-icon-button {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  color: var(--geni-text-muted);
  background: color-mix(in srgb, var(--geni-text) 6%, transparent);
  transition: background 0.2s ease, color 0.2s ease, transform 0.3s var(--geni-spring);
}

.geni-icon-button svg {
  width: 15px;
  height: 15px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.9;
  stroke-linecap: round;
}

.geni-icon-button:hover {
  color: var(--geni-text);
  background: color-mix(in srgb, var(--geni-text) 11%, transparent);
  transform: scale(1.08);
}

.geni-icon-button:active {
  transform: scale(0.94);
}

/* ── Scroll region ───────────────────────────────────────────────────────── */
.geni-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 14px 16px 6px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--geni-text) 22%, transparent) transparent;
}

.geni-scroll::-webkit-scrollbar { width: 6px; }
.geni-scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: color-mix(in srgb, var(--geni-text) 20%, transparent);
}

/* ── Conversation ────────────────────────────────────────────────────────── */
.geni-messages {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.geni-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
  animation: geni-bubble-in 0.52s var(--geni-spring) both;
}

.geni-row--player { align-items: flex-end; }
.geni-row--assistant { align-items: flex-start; }

@keyframes geni-bubble-in {
  from { opacity: 0; transform: translate3d(0, 10px, 0) scale(0.97); }
  to   { opacity: 1; transform: none; }
}

.geni-message {
  max-width: 85%;
  padding: 10px 13px;
  border-radius: 18px;
  font-size: 13.5px;
  line-height: 1.45;
  letter-spacing: -0.005em;
  /* The reply is a language model's output. It arrives here as textContent and
     nothing in this sheet can make it behave otherwise, but long unbroken
     strings must not push the panel wider than the viewport. */
  overflow-wrap: anywhere;
}

.geni-message--player {
  color: var(--geni-on-accent);
  background: linear-gradient(150deg, var(--geni-accent), var(--geni-accent-deep));
  border-bottom-right-radius: 6px;
  box-shadow:
    0 4px 14px color-mix(in srgb, var(--geni-accent) 32%, transparent),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.geni-message--assistant {
  background: var(--geni-glass-raised);
  border: 1px solid var(--geni-hairline);
  border-bottom-left-radius: 6px;
  box-shadow: inset 0 1px 0 var(--geni-bevel-dim);
}

.geni-message-time {
  padding: 0 6px;
  font-size: 10.5px;
  font-weight: 500;
  color: var(--geni-text-faint);
}

/* Three dots that breathe. Deliberately not a spinner: a spinner says "the page
   is loading", these say "someone is composing an answer". */
.geni-thinking {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  padding: 0 2px;
  font-size: 12.5px;
  color: var(--geni-text-muted);
}

.geni-thinking-dots {
  display: inline-flex;
  gap: 4px;
}

.geni-thinking-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--geni-accent);
  animation: geni-dot 1.25s ease-in-out infinite;
}

.geni-thinking-dots span:nth-child(2) { animation-delay: 0.16s; }
.geni-thinking-dots span:nth-child(3) { animation-delay: 0.32s; }

@keyframes geni-dot {
  0%, 60%, 100% { transform: translateY(0) scale(0.82); opacity: 0.42; }
  30%           { transform: translateY(-4px) scale(1); opacity: 1; }
}

/* ── Bet slip ────────────────────────────────────────────────────────────── */
.geni-slip-title {
  margin: 4px 2px -2px;
  font-size: 11px;
  font-weight: 640;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--geni-text-faint);
}

.geni-slip {
  position: relative;
  border-radius: 18px;
  padding: 13px;
  display: flex;
  flex-direction: column;
  gap: 11px;
  background: var(--geni-glass-raised);
  /* No backdrop-filter, on purpose. It had the same rounded-corner defect as
     the panel, and the fix here is to drop it rather than to add another layer:
     this card sits on top of glass that has already blurred the page, so a
     second blur of the same backdrop costs a compositing pass per card and
     changes almost nothing. The tint and the bevel are what make it read as a
     raised pane. */
  box-shadow:
    0 6px 20px rgba(16, 12, 32, 0.08),
    inset 0 1px 0 var(--geni-bevel-bright);
  animation: geni-slip-in 0.66s var(--geni-spring) both;
}

.geni-slip::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(
    150deg,
    var(--geni-bevel-bright),
    transparent 46%,
    color-mix(in srgb, var(--geni-accent) 30%, transparent)
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

@keyframes geni-slip-in {
  from { opacity: 0; transform: translate3d(0, 14px, 0) scale(0.97); }
  to   { opacity: 1; transform: none; }
}

.geni-slip-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--geni-hairline);
}

.geni-slip-event {
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.geni-slip-count {
  margin-left: auto;
  flex: none;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 620;
  letter-spacing: 0.02em;
  color: var(--geni-accent);
  background: color-mix(in srgb, var(--geni-accent) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--geni-accent) 22%, transparent);
}

.geni-legs {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.geni-leg {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 4px 10px;
}

.geni-leg-event {
  grid-column: 1;
  font-size: 11px;
  font-weight: 500;
  color: var(--geni-text-faint);
}

.geni-leg-market {
  grid-column: 1;
  font-size: 11.5px;
  color: var(--geni-text-muted);
}

.geni-leg-selection {
  grid-column: 1;
  font-size: 13px;
  font-weight: 560;
  letter-spacing: -0.008em;
}

/* The live odds badge. Monospaced figures so a price changing does not shift
   the layout, and a brief flare when it does — see .geni-leg-price--moved. */
.geni-leg-price {
  grid-column: 2;
  grid-row: 1 / -1;
  align-self: center;
  padding: 5px 10px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 640;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  color: var(--geni-accent);
  background: color-mix(in srgb, var(--geni-accent) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--geni-accent) 20%, transparent);
  box-shadow: inset 0 1px 0 var(--geni-bevel-dim);
  transition: background 0.3s ease, transform 0.3s var(--geni-spring);
}

.geni-total {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 0;
  padding-top: 10px;
  border-top: 1px solid var(--geni-hairline);
}

.geni-total-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--geni-text-muted);
}

.geni-total-price {
  margin-left: auto;
  font-size: 22px;
  font-weight: 680;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  background: linear-gradient(140deg, var(--geni-accent), var(--geni-accent-deep));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.geni-confirm {
  width: 100%;
  height: 44px;
  border: 0;
  border-radius: 14px;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--geni-on-accent);
  cursor: pointer;
  background: linear-gradient(150deg, var(--geni-accent), var(--geni-accent-deep));
  box-shadow:
    0 6px 18px color-mix(in srgb, var(--geni-accent) 34%, transparent),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  transition: transform 0.34s var(--geni-spring), box-shadow 0.34s var(--geni-spring), filter 0.2s ease;
}

.geni-confirm:hover:not([disabled]) {
  transform: translateY(-1.5px);
  box-shadow:
    0 10px 26px color-mix(in srgb, var(--geni-accent) 42%, transparent),
    inset 0 1px 0 rgba(255, 255, 255, 0.34);
}

.geni-confirm:active:not([disabled]) {
  transform: translateY(0) scale(0.988);
}

.geni-confirm[disabled] {
  cursor: not-allowed;
  filter: saturate(30%);
  opacity: 0.55;
  box-shadow: none;
}

/* A price move is the one moment the bettor must not skim past, so it gets the
   only amber in the palette and the old number with a line through it. */
.geni-price-moved {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  margin: 0;
  padding: 9px 11px;
  border-radius: 12px;
  font-size: 12px;
  line-height: 1.4;
  color: #8a5a05;
  background: color-mix(in srgb, #f5a524 16%, transparent);
  border: 1px solid color-mix(in srgb, #f5a524 34%, transparent);
}

@media (prefers-color-scheme: dark) {
  :host(:not([theme="light"])) .geni-price-moved { color: #f3c677; }
}
:host([theme="dark"]) .geni-price-moved { color: #f3c677; }

.geni-price-moved-previous {
  font-weight: 640;
  font-variant-numeric: tabular-nums;
  text-decoration: line-through;
  opacity: 0.75;
}

.geni-price-moved-action { flex-basis: 100%; }

/* ── Openers ─────────────────────────────────────────────────────────────────
   Deliberately unlike the stake chips. Those are accented pills carrying money;
   these are quiet outlined buttons carrying a sentence, and a bettor who has
   learnt what the accent means should never find it on something that is not an
   amount. No backdrop-filter, on the same terms as everything else in here. */
.geni-starters {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 2px 0 4px;
  animation: geni-slip-in 0.66s var(--geni-spring) both;
}

/* The written introduction above the chips. Deliberately not styled as an
   assistant bubble: it is not something the assistant said, and dressing a
   fixture up as a turn in the conversation would be the panel telling a small
   lie about where its words come from. */
.geni-greeting {
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--geni-text-muted);
}

.geni-starters-title {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--geni-text-faint);
}

.geni-starter-list {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.geni-starter {
  padding: 8px 13px;
  border-radius: 15px;
  font: inherit;
  font-size: 13px;
  line-height: 1.25;
  text-align: left;
  cursor: pointer;
  color: var(--geni-text);
  background: var(--geni-glass-sunken);
  border: 1px solid var(--geni-hairline);
  transition: transform 0.3s var(--geni-spring), background 0.2s ease, border-color 0.2s ease;
}

.geni-starter:hover {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--geni-accent) 40%, transparent);
  background: color-mix(in srgb, var(--geni-accent) 8%, transparent);
}

.geni-starter:active { transform: translateY(0) scale(0.98); }

/* ── Naming an amount ────────────────────────────────────────────────────────
   Sits directly under the slip it belongs to and shares its sunken tint, so the
   two read as one surface with a question at the bottom rather than as a card
   and an unrelated form. No backdrop-filter anywhere below, for the reason
   .geni-slip states: this is all on glass that has already blurred the page. */
.geni-stake {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 12px 13px;
  border-radius: 16px;
  background: var(--geni-glass-sunken);
  border: 1px solid var(--geni-hairline);
  animation: geni-slip-in 0.66s var(--geni-spring) both;
}

.geni-stake-balance {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin: 0;
  font-size: 12px;
  color: var(--geni-text-muted);
}

/* Deliberately not styled as a warning. The balance is a neutral fact even
   when the amount is over it; the sentence below is where a problem is named,
   and colouring both would make an ordinary number look like an error. */
.geni-stake-balance-value {
  font-weight: 640;
  font-variant-numeric: tabular-nums;
  color: var(--geni-text);
}

/* Amber, matching the price-move notice, because it is the same kind of thing:
   something the bettor has to read before the next tap makes sense. Not red —
   asking for more than is in the account is ordinary, not a fault. */
.geni-stake-problem {
  margin: 0;
  padding: 8px 10px;
  border-radius: 11px;
  font-size: 12px;
  line-height: 1.4;
  color: #8a5a05;
  background: color-mix(in srgb, #f5a524 14%, transparent);
  border: 1px solid color-mix(in srgb, #f5a524 30%, transparent);
}

@media (prefers-color-scheme: dark) {
  :host(:not([theme="light"])) .geni-stake-problem { color: #f3c677; }
}
:host([theme="dark"]) .geni-stake-problem { color: #f3c677; }

.geni-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

/* ── Building on a slip ──────────────────────────────────────────────────────
   Smaller than the choice chips below, and that is the point rather than a
   detail. Those are a decision between four matches; these sit under a bet
   already settled on and are offered in passing. Anything that competed with
   the confirm button would be something pressed by mistake on the one screen
   where pressing moves money. */
.geni-build {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin: 2px 0 4px;
}

.geni-build-title {
  margin: 0;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--geni-muted);
}

/* The disclosure and its contents share one box, so opening reads as this
   control growing rather than as another block arriving underneath it. Closed,
   the box is exactly the button; open, the button is its lid. */
.geni-build-box {
  display: flex;
  flex-direction: column;
  gap: 0;
  align-self: flex-start;
  max-width: 100%;
  border-radius: 15px;
  border: 1px solid color-mix(in srgb, var(--geni-text) 12%, transparent);
  background: color-mix(in srgb, var(--geni-text) 5%, transparent);
  transition: border-color 0.2s ease, background 0.2s ease;
}

.geni-build-box--open {
  align-self: stretch;
  gap: 8px;
  padding-bottom: 10px;
  border-color: color-mix(in srgb, var(--geni-accent) 24%, transparent);
  background: color-mix(in srgb, var(--geni-accent) 7%, transparent);
}

/* Closed by default, and that is the whole design: a slip somebody has settled
   on should not grow a shopping aisle underneath it. One quiet control, and
   the aisle only if they ask. */
.geni-build-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  align-self: flex-start;
  padding: 5px 12px;
  border-radius: 15px;
  font: inherit;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  color: var(--geni-muted);
  background: transparent;
  border: none;
  transition: color 0.2s ease;
}

.geni-build-toggle:hover { color: var(--geni-text); }
.geni-build-toggle--open { color: var(--geni-accent); }

.geni-build-caret {
  flex: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: transform 0.3s var(--geni-spring);
}

.geni-build-toggle--open .geni-build-caret { transform: rotate(180deg); }

.geni-build-options {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 0 10px;
}

.geni-build-confirm {
  align-self: flex-start;
  margin: 0 10px;
  padding: 7px 16px;
  border-radius: 16px;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.01em;
  cursor: pointer;
  color: var(--geni-on-accent, #ffffff);
  background: var(--geni-accent);
  border: 1px solid transparent;
  box-shadow: 0 2px 8px color-mix(in srgb, var(--geni-accent) 30%, transparent);
  transition: transform 0.3s var(--geni-spring), filter 0.2s ease;
}

.geni-build-confirm:hover { filter: brightness(1.08); }
.geni-build-confirm:active { transform: scale(0.97); }

.geni-build-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  max-width: 100%;
  padding: 5px 11px;
  border-radius: 15px;
  font: inherit;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.35;
  cursor: pointer;
  color: var(--geni-accent);
  /* The stake chip's material at a smaller scale, and no blur of its own.
     These sit *on* the panel, which is already glass — filtering the same
     backdrop twice buys nothing, and a backdrop-filter on a rounded element is
     the corner artifact the layer comment above exists to avoid. What makes it
     read as glass rather than as a sticker is the rest: an accent tint, a
     specular top edge and the faintest lift off the surface. The first version
     had none of those and looked pasted on. */
  background: color-mix(in srgb, var(--geni-accent) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--geni-accent) 24%, transparent);
  box-shadow:
    inset 0 1px 0 color-mix(in srgb, #ffffff 14%, transparent),
    0 1px 2px color-mix(in srgb, #000000 10%, transparent);
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.3s var(--geni-spring);
}

.geni-build-chip:hover:not([disabled]) {
  background: color-mix(in srgb, var(--geni-accent) 16%, transparent);
  border-color: color-mix(in srgb, var(--geni-accent) 34%, transparent);
}

.geni-build-chip:active:not([disabled]) { transform: scale(0.97); }
.geni-build-chip[disabled] { opacity: 0.5; cursor: default; }

/* Ticked reads as filled rather than as outlined, so a glance counts what is
   going in without reading any of it.
 *
 * **After the base rule, and that is the whole fix.** These two selectors
 * carry the same weight, so the later one wins — and this sat above
 * .geni-build-chip, whose own background, border and color quietly
 * overrode every line of it. Ticking a chip changed aria-pressed and
 * nothing a bettor could see: pick three and there was no way to tell which
 * three. Written months ago, never once applied, and invisible to every test
 * here because they all assert state rather than paint. Found by pressing it.
 */
.geni-build-chip--picked {
  color: var(--geni-on-accent, #ffffff);
  background: var(--geni-accent);
  border-color: var(--geni-accent);
}

.geni-build-chip--picked:hover:not([disabled]) {
  background: var(--geni-accent-deep, var(--geni-accent));
  border-color: var(--geni-accent-deep, var(--geni-accent));
}

.geni-build-chip--picked .geni-build-chip-price { color: inherit; opacity: 0.85; }

.geni-build-chip-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.geni-build-chip-price {
  flex: none;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--geni-accent);
}

/* The catch-all, for the bet the three chips did not think of. Quieter than
   the composer at the foot of the panel, because that one is the main way in
   and this is a footnote to a slip. */
.geni-build-field {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 13px;
  border-radius: 16px;
  font: inherit;
  font-size: 12px;
  color: var(--geni-text);
  /* Same family, no accent: this is the way out for a bet the three chips did
     not think of, and it should read as a field rather than as a fourth option
     to press. */
  background: color-mix(in srgb, var(--geni-text) 5%, transparent);
  border: 1px solid color-mix(in srgb, var(--geni-text) 14%, transparent);
  box-shadow: inset 0 1px 0 color-mix(in srgb, #ffffff 8%, transparent);
  transition: border-color 0.2s ease;
}

.geni-build-field::placeholder { color: var(--geni-muted); }
.geni-build-field:focus {
  outline: none;
  border-color: color-mix(in srgb, var(--geni-accent) 40%, transparent);
}

/* The field, and under it the button that says what to use it for.
   Inside the field the plus read as decoration; beside it, labelled "Add", it
   was legible and still did not say add *what*. Under the field, full width and
   naming the thing, it is the invitation to reach a different match — which is
   the half of this row nobody was finding. */
.geni-build-add {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Field and arrow on one line, the field taking whatever is left. */
.geni-build-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.geni-build-add .geni-build-field {
  flex: 1 1 auto;
  min-width: 0;
}

/* The composer's send button, one size down: this row is an aside under a
   slip, and an arrow the size of the one that starts a bet would compete with
   it. Everything else about it is inherited on purpose — it is the same act. */
.geni-build-send {
  width: 30px;
  height: 30px;
}

.geni-build-send svg {
  width: 14px;
  height: 14px;
}

.geni-build-add-button {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 13px;
  border-radius: 16px;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  /* The stake chip's material, because it is the same kind of thing: a small
     positive action beside a field. */
  color: var(--geni-accent);
  background: color-mix(in srgb, var(--geni-accent) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--geni-accent) 34%, transparent);
  box-shadow: inset 0 1px 0 color-mix(in srgb, #ffffff 10%, transparent);
  transition: background 0.2s ease, border-color 0.2s ease;
}

.geni-build-add-button:hover {
  background: color-mix(in srgb, var(--geni-accent) 20%, transparent);
  border-color: color-mix(in srgb, var(--geni-accent) 48%, transparent);
}

.geni-build-add-button:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--geni-accent) 55%, transparent);
  outline-offset: 1px;
}

.geni-build-plus {
  stroke: currentColor;
  stroke-linecap: round;
  fill: none;
}

/* Taking one leg back off the ticket.
   Quiet until reached for: it sits on the last screen before money moves, and
   a loud button beside every selection would read as a warning about the bet
   rather than as a way to edit it. */
.geni-card-leg-remove {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-left: 6px;
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  color: var(--geni-muted);
  background: color-mix(in srgb, var(--geni-text) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--geni-text) 12%, transparent);
  transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

.geni-card-leg-remove:hover {
  color: var(--geni-accent);
  background: color-mix(in srgb, var(--geni-accent) 14%, transparent);
  border-color: color-mix(in srgb, var(--geni-accent) 34%, transparent);
}

.geni-card-leg-remove:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--geni-accent) 55%, transparent);
  outline-offset: 1px;
}

.geni-card-leg-minus {
  stroke: currentColor;
  stroke-linecap: round;
  fill: none;
}

/* ── Choosing between matches ────────────────────────────────────────────────
   Built from the stake chip rather than from the slip card, because it is the
   same act: a short row of small things to press while nothing is decided yet.
   Wider than a money chip and full-width in a column, since a fixture name is
   long and two of them side by side would each wrap to three lines. */
.geni-choices {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 2px 0 4px;
}

.geni-choice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 38px;
  padding: 8px 14px;
  border-radius: 19px;
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  color: var(--geni-accent);
  background: color-mix(in srgb, var(--geni-accent) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--geni-accent) 26%, transparent);
  transition: transform 0.3s var(--geni-spring), background 0.2s ease;
}

.geni-choice:hover:not([disabled]) {
  background: color-mix(in srgb, var(--geni-accent) 17%, transparent);
}

.geni-choice:active:not([disabled]) { transform: scale(0.985); }
.geni-choice[disabled] { opacity: 0.5; cursor: default; }

.geni-choice-label {
  font-weight: 600;
  letter-spacing: -0.01em;
}

/* The number is what they are comparing, so it is given its own weight and a
   tabular figure — prices that do not line up are prices you have to read
   twice. */
.geni-choice-price {
  flex: none;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.geni-chip {
  height: 34px;
  padding: 0 13px;
  border-radius: 17px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  cursor: pointer;
  color: var(--geni-accent);
  background: color-mix(in srgb, var(--geni-accent) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--geni-accent) 26%, transparent);
  transition: transform 0.3s var(--geni-spring), background 0.2s ease;
}

.geni-chip:hover:not([disabled]) {
  transform: translateY(-1px);
  background: color-mix(in srgb, var(--geni-accent) 17%, transparent);
}

.geni-chip:active:not([disabled]) { transform: translateY(0) scale(0.97); }

.geni-chip[disabled] { cursor: not-allowed; opacity: 0.45; }

/* The deposit shortcut is deliberately the quietest thing on the panel. It is
   shown only where the operator has a flow and the jurisdiction permits one,
   and where it does appear it is a route somewhere rather than an invitation —
   so it carries no accent, no fill and no lift. */
.geni-chip--topup {
  color: var(--geni-text-muted);
  background: transparent;
  border-color: var(--geni-hairline);
}

.geni-chip--topup:hover:not([disabled]) {
  background: color-mix(in srgb, var(--geni-text) 6%, transparent);
}

.geni-stake-field {
  display: flex;
  align-items: center;
  gap: 9px;
}

.geni-stake-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--geni-text-muted);
}

.geni-stake-input {
  flex: 1;
  min-width: 0;
  height: 38px;
  padding: 0 12px;
  border-radius: 12px;
  font: inherit;
  font-size: 15px;
  font-weight: 640;
  font-variant-numeric: tabular-nums;
  color: var(--geni-text);
  background: var(--geni-glass-raised);
  border: 1px solid var(--geni-hairline);
}

.geni-stake-input:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--geni-accent) 60%, transparent);
  outline-offset: 1px;
}

.geni-stake-input[disabled] { cursor: not-allowed; opacity: 0.55; }

/* ── The confirmation card ───────────────────────────────────────────────────
   The one surface in the widget that carries an accent border, because it is
   the one surface where pressing something moves money. Everything being
   agreed to is on it, above the button that agrees. */
.geni-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 14px;
  border-radius: 18px;
  background: var(--geni-glass-raised);
  border: 1px solid color-mix(in srgb, var(--geni-accent) 34%, transparent);
  box-shadow:
    0 8px 24px color-mix(in srgb, var(--geni-accent) 14%, transparent),
    inset 0 1px 0 var(--geni-bevel-bright);
  animation: geni-slip-in 0.66s var(--geni-spring) both;
}

.geni-card-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.geni-card-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--geni-accent);
}

.geni-card-sport {
  font-size: 11px;
  color: var(--geni-text-faint);
}

.geni-card-event {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--geni-text);
}

.geni-card-legs {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.geni-card-leg {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  font-size: 12.5px;
  color: var(--geni-text-muted);
}

.geni-card-leg-price {
  font-weight: 640;
  font-variant-numeric: tabular-nums;
  color: var(--geni-text);
}

.geni-card-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin: 0;
  font-size: 13px;
  color: var(--geni-text-muted);
}

.geni-card-row--odds { padding-top: 9px; border-top: 1px solid var(--geni-hairline); }

.geni-card-value {
  font-weight: 640;
  font-variant-numeric: tabular-nums;
  color: var(--geni-text);
}

/* The payout is the number a bettor checks last and hardest, so it is the only
   figure on the card given the accent and a larger size. Stating what a bet
   returns is arithmetic on numbers already on screen — it is not a claim about
   whether it will. */
.geni-card-row--payout { font-size: 14px; }
.geni-card-row--payout .geni-card-value {
  font-size: 17px;
  font-weight: 700;
  color: var(--geni-accent);
}

.geni-place {
  width: 100%;
  height: 46px;
  margin-top: 2px;
  border: 0;
  border-radius: 14px;
  font: inherit;
  font-size: 15px;
  font-weight: 660;
  letter-spacing: -0.01em;
  color: var(--geni-on-accent);
  cursor: pointer;
  background: linear-gradient(150deg, var(--geni-accent), var(--geni-accent-deep));
  box-shadow:
    0 8px 22px color-mix(in srgb, var(--geni-accent) 38%, transparent),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  transition: transform 0.34s var(--geni-spring), box-shadow 0.34s var(--geni-spring), filter 0.2s ease;
}

.geni-place:hover:not([disabled]) {
  transform: translateY(-1.5px);
  box-shadow:
    0 12px 30px color-mix(in srgb, var(--geni-accent) 46%, transparent),
    inset 0 1px 0 rgba(255, 255, 255, 0.34);
}

.geni-place:active:not([disabled]) { transform: translateY(0) scale(0.988); }

.geni-place[disabled] {
  cursor: not-allowed;
  filter: saturate(30%);
  opacity: 0.55;
  box-shadow: none;
}

/* ── Notices ─────────────────────────────────────────────────────────────── */
.geni-placement,
.geni-notice,
.geni-error,
.geni-unavailable {
  margin: 0;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 12.5px;
  line-height: 1.45;
  border: 1px solid var(--geni-hairline);
  background: var(--geni-glass-sunken);
  color: var(--geni-text-muted);
}

.geni-error {
  color: #b4232b;
  background: color-mix(in srgb, #e5484d 12%, transparent);
  border-color: color-mix(in srgb, #e5484d 30%, transparent);
}

@media (prefers-color-scheme: dark) {
  :host(:not([theme="light"])) .geni-error { color: #ff9ea1; }
}
:host([theme="dark"]) .geni-error { color: #ff9ea1; }

/* Sits above the transcript and closes the input with it, so a jurisdiction
   where the assistant may not operate reads as a statement rather than as a
   box that keeps refusing. */
.geni-unavailable {
  margin: 14px 16px 0;
  font-weight: 500;
}

/* ── Composer ────────────────────────────────────────────────────────────── */
.geni-composer {
  position: relative;
  z-index: 4;
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 8px 14px 12px;
  padding: 6px 6px 6px 15px;
  border-radius: 999px;
  background: var(--geni-glass-sunken);
  border: 1px solid var(--geni-hairline);
  box-shadow: inset 0 1px 2px rgba(16, 12, 32, 0.05);
  transition: border-color 0.24s ease, box-shadow 0.24s ease;
}

.geni-composer:focus-within {
  border-color: color-mix(in srgb, var(--geni-accent) 52%, transparent);
  box-shadow:
    inset 0 1px 2px rgba(16, 12, 32, 0.05),
    0 0 0 4px color-mix(in srgb, var(--geni-accent) 15%, transparent);
}

.geni-input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 13.5px;
  color: var(--geni-text);
  padding: 9px 0;
}

.geni-input::placeholder { color: var(--geni-text-faint); }
.geni-input:focus { outline: none; }
.geni-input[disabled] { cursor: not-allowed; }

.geni-send {
  position: relative;
  flex: none;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  color: var(--geni-on-accent);
  background: linear-gradient(150deg, var(--geni-accent), var(--geni-accent-deep));
  box-shadow:
    0 4px 12px color-mix(in srgb, var(--geni-accent) 36%, transparent),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  transition: transform 0.34s var(--geni-spring), filter 0.2s ease;
}

/* The label is for screen readers; the icon is what is seen. */
.geni-send-label {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.geni-send svg {
  width: 17px;
  height: 17px;
  fill: currentColor;
  transform: translateX(0.5px);
}

.geni-send:hover:not([disabled]) { transform: scale(1.08) rotate(-8deg); }
.geni-send:active:not([disabled]) { transform: scale(0.94); }
.geni-send[disabled] { cursor: not-allowed; filter: saturate(25%); opacity: 0.5; }

/* ── Responsible gambling ────────────────────────────────────────────────── */
.geni-rg {
  padding: 9px 16px 10px;
  border-top: 1px solid var(--geni-hairline);
  background: color-mix(in srgb, var(--geni-text) 3%, transparent);
  display: flex;
  flex-direction: column;
  gap: 3px;
}

/* Deliberately not the faintest token available. This is the responsible
   gambling notice and the age limit — the one text in the panel a regulator
   would ask about — and styling compliance copy so it is technically present
   but hard to read is the oldest trick in the book. Small and quiet, yes;
   greyed out to the edge of legibility, no. */
.geni-rg-message,
.geni-rg-age {
  margin: 0;
  font-size: 11px;
  line-height: 1.5;
  color: var(--geni-text-muted);
}

.geni-rg-age { font-weight: 580; }

/* The line that never folds away. Same treatment as the full wording, because
   it IS the compliance copy when the rest is closed — and a short notice
   styled fainter than a long one would be the same trick at half the size. */
.geni-rg-short {
  margin: 7px 0 0;
  font-size: 11px;
  line-height: 1.5;
  color: var(--geni-text-muted);
  text-align: center;
}

/* Above the buttons, so expanding does not push what was just pressed out
   from under the finger on a short panel. */
.geni-rg-full {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 6px;
  animation: geni-slip-in 0.4s var(--geni-spring) both;
}

.geni-footer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

/* Shares the chip shape with the feedback button beside it. Two controls of
   one weight, because neither is the thing the panel is for. */
.geni-rg-open {
  padding: 3px 11px;
  border-radius: 11px;
  font: inherit;
  font-size: 11px;
  line-height: 1.3;
  cursor: pointer;
  color: var(--geni-text-muted);
  background: var(--geni-glass-sunken);
  border: 1px solid var(--geni-hairline);
  transition: transform 0.3s var(--geni-spring), background 0.2s ease,
    border-color 0.2s ease, color 0.2s ease;
}

.geni-rg-open:hover {
  transform: translateY(-1px);
  color: var(--geni-text);
  border-color: color-mix(in srgb, var(--geni-accent) 40%, transparent);
  background: color-mix(in srgb, var(--geni-accent) 8%, transparent);
}

.geni-rg-open:active { transform: translateY(0) scale(0.98); }

/* ── Saying what you thought of the answer ───────────────────────────────── */
/* Quiet on purpose, and quieter than anything it sits under. This is a comment
   on the answer rather than a step in the bet, and on a screen where pressing
   something moves money it must not read as one of the things that does. No
   accent, no fill, no border until it is under the pointer. */
.geni-feedback {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 2px 0 0;
  align-items: center;
}

.geni-feedback-vote,
.geni-feedback-reason {
  font: inherit;
  font-size: 11.5px;
  line-height: 1.3;
  color: var(--geni-text-muted);
  background: none;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 3px 9px;
  cursor: pointer;
  transition: color 120ms ease, border-color 120ms ease;
}

.geni-feedback-vote:hover,
.geni-feedback-reason:hover {
  color: var(--geni-text);
  border-color: var(--geni-border);
}

/* The three reasons are a question that was asked, so they carry a little more
   weight than the two taps that opened them — enough to read as a prompt
   waiting for an answer rather than as more of the same. */
.geni-feedback--why .geni-feedback-reason { border-color: var(--geni-border); }

.geni-feedback--done {
  font-size: 11.5px;
  color: var(--geni-text-muted);
  margin: 2px 0 0;
}

/* The standing way in, centred under the responsible-gambling notice.
   Shaped like the opener chips above it rather than as an underlined link: a
   link reads as navigation, and the first thing this said — "How is the
   assistant doing?" — read as a question being asked OF the bettor rather
   than an invitation to answer one. Chip plus imperative wording removes both
   readings at once, and it costs nothing when nobody presses it. */
/* Sized down from the opener chips it borrows from, deliberately. Those are
   the point of an empty panel; this is a footnote under a compliance notice,
   and at full chip size it doubled the footer and pulled the eye to the least
   important thing on screen. Small enough to be background, large enough to
   read as pressable. */
.geni-feedback-open {
  padding: 3px 11px;
  border-radius: 11px;
  font: inherit;
  font-size: 11px;
  line-height: 1.3;
  cursor: pointer;
  color: var(--geni-text-muted);
  background: var(--geni-glass-sunken);
  border: 1px solid var(--geni-hairline);
  transition: transform 0.3s var(--geni-spring), background 0.2s ease,
    border-color 0.2s ease, color 0.2s ease;
}

.geni-feedback-open:hover {
  transform: translateY(-1px);
  color: var(--geni-text);
  border-color: color-mix(in srgb, var(--geni-accent) 40%, transparent);
  background: color-mix(in srgb, var(--geni-accent) 8%, transparent);
}

.geni-feedback-open:active { transform: translateY(0) scale(0.98); }

.geni-feedback-form {
  border-top: 1px solid var(--geni-border);
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--geni-surface);
}

.geni-feedback-form-title {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--geni-text);
}

.geni-feedback-text {
  font: inherit;
  font-size: 13px;
  line-height: 1.45;
  color: var(--geni-text);
  background: var(--geni-input-bg, var(--geni-surface));
  border: 1px solid var(--geni-border);
  border-radius: 8px;
  padding: 8px 10px;
  resize: vertical;
  min-height: 56px;
}

.geni-feedback-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.geni-feedback-cancel,
.geni-feedback-send {
  font: inherit;
  font-size: 12px;
  border-radius: 999px;
  padding: 5px 14px;
  cursor: pointer;
  border: 1px solid var(--geni-border);
  background: none;
  color: var(--geni-text-muted);
}

.geni-feedback-send {
  border-color: transparent;
  background: var(--geni-accent);
  color: var(--geni-accent-ink, #fff);
  font-weight: 600;
}

/* ── Focus ───────────────────────────────────────────────────────────────── */
:where(button, input):focus-visible {
  outline: 2px solid var(--geni-accent);
  outline-offset: 2px;
}

/* ── Motion ──────────────────────────────────────────────────────────────── */
/* Everything above is decoration over a layout that is already correct, so the
   honest response to this preference is to remove it rather than to shorten it. */
@media (prefers-reduced-motion: reduce) {
  /* The glyph is the resting state anyway, so removing the motion leaves a
     clean sport icon rather than a gap — decoration over a layout that is
     already correct, which is the rule the panel entrance already follows. The
     travelling dot goes entirely: a dot parked on a target is a smudge, not a
     picture of anything. */
  .geni-stage-ball,
  .geni-stage-target {
    animation: none;
  }
  .geni-stage-ball { opacity: 0; }

  *,
  *::before,
  *::after {
    animation: none !important;
    transition-duration: 0.01ms !important;
  }
}

/* ── Narrow viewports ────────────────────────────────────────────────────── */
@media (max-width: 460px) {
  :host {
    right: 12px;
    bottom: 12px;
    left: 12px;
  }
  .geni-panel {
    width: 100%;
    max-height: calc(100vh - 28px);
  }
}
`;
